import React, { useRef, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  FormControlLabel,
  Popover,
  Radio,
  RadioGroup,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';

import { useLanguage } from '../../hooks';
import { Checkbox } from '../uiComponents/ComponentLibrary/Checkbox';

const customStyles: Record<string, SxProps<Theme>> = {
  setting: {
    display: 'flex',
    alignItems: 'center',
    marginRight: '22px',
    '&:last-child': {
      marginRight: '0px',
    },
  },
  expandable: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    borderRadius: '8px',
    paddingLeft: '0.25em',
    '&:hover': {
      backgroundColor: 'rgba(32,33,36,0.039)',
    },
  },
  closeIcon: {
    transition: 'transform .1s ease-in-out',
    transform: 'rotate(180deg)',
    MsTransform: 'rotate(180deg)',
    WebkitTransform: 'rotate(180deg)',
  },
  openIcon: {
    transition: 'transform .1s ease-out',
  },
  config: {
    padding: '10px',
    borderRadius: '8px',
  },
  samplingBox: {
    maxWidth: '190px',
  },
  caption: {
    color: '#6E7178',
  },
};

interface NumberPercentSettingProps {
  showPercent: boolean;
  showSampling: boolean;
  onShowChange: (show: 'percent' | 'sampling' | null) => void;
}

const NumberPercentSetting: React.FC<NumberPercentSettingProps> = ({
  showPercent,
  showSampling,
  onShowChange: handleValidationChange,
}) => {
  const lang = useLanguage();

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  const handleOpenConfig = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    handleValidationChange(value as 'percent' | 'sampling' | null);
  };

  const open = Boolean(anchorEl);
  const selectedValidation = showPercent ? 'percent' : showSampling ? 'sampling' : null;

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.checked) {
      handleValidationChange(null);
      return;
    }
    setAnchorEl(anchorRef.current);
    handleValidationChange('percent');
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Checkbox
        size='small'
        checked={Boolean(showPercent) || Boolean(showSampling)}
        onChange={handleCheckboxChange}
        onClick={(event) => event.stopPropagation()}
      />
      <Box
        sx={customStyles.expandable}
        onClick={handleOpenConfig}
        ref={anchorRef}
      >
        <Typography variant='body1'>{lang.percentage}</Typography>
        <ExpandMoreIcon sx={anchorEl ? customStyles.closeIcon : customStyles.openIcon} />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        elevation={5}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box sx={customStyles.config}>
          <RadioGroup
            value={selectedValidation}
            onChange={handleOptionChange}
          >
            <FormControlLabel
              value='percent'
              control={
                <Radio
                  size='small'
                  color='primary'
                />
              }
              label={lang.form_builder.showPercent}
            />
            <FormControlLabel
              value='sampling'
              control={
                <Radio
                  size='small'
                  color='primary'
                />
              }
              label={
                <Box sx={customStyles.samplingBox}>
                  <Typography>{`${lang.form_builder.showSamplingPercent} `}</Typography>
                  <Typography
                    variant='caption'
                    sx={customStyles.caption}
                  >
                    {lang.form_builder.availableAppWeb}
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </Box>
      </Popover>
    </Box>
  );
};

export default NumberPercentSetting;
